// Shared between generic_crud.tsx (master grid) and ChildDetailPanel.tsx (child-detail grid) -
// extracted so the same logic isn't duplicated across the docked page and the undocked detail
// window (REQ-DU-013/014: reuse one component/logic set in both modes, no forked copies).
import type { ColDef } from "ag-grid-community";
import type { GetContextMenuItemsParams, MenuItemDef } from "ag-grid-enterprise";
import type { ActionMeta, EntityMeta } from "../../schemas/crudMeta";
import { buildDefaultColDef } from "../../util/AgGridFunctions";
import type { ColumnDefaultsResponse } from "../../types";
import GridHeaderTooltip from "../PersistentGrid/GridHeaderTooltip";

export type Row = Record<string, unknown> & { __tempId?: string };

export function newTempId(): string {
  return `new-${Date.now()}-${Math.random().toString(36).slice(2)}`;
}

export function buildColumnDefs(entity: EntityMeta, editable: boolean, columnDefaults?: ColumnDefaultsResponse | null): ColDef<Row>[] {
  return entity.columns
    .filter((c) => c.visibility !== "none")
    .map((c) => {
      const colEditable = editable && c.editable;
      // gui.column_default is keyed by column_name only now (flat, no type tiering) - this is
      // just an optional floor if a matching row exists for this field name; Generic CRUD's own
      // EntityMeta.type switch below is what actually picks filter/editor per column.
      const typeFloor = buildDefaultColDef(columnDefaults, c.field);
      if (c.type === "number" || c.type === "lookup") {
        // "lookup" columns edit as a plain FK-id number for now - resolving to the related
        // record's display title via a real picker (INT-09) is still-pending scope.
        return { ...typeFloor, headerName: c.label, field: c.field, filter: "agNumberColumnFilter", editable: colEditable, cellEditor: "agNumberCellEditor" } as ColDef<Row>;
      }
      if (c.type === "bool") {
        return { ...typeFloor, headerName: c.label, field: c.field, filter: "agSetColumnFilter", editable: colEditable, cellRenderer: "agCheckboxCellRenderer", cellEditor: "agCheckboxCellEditor" } as ColDef<Row>;
      }
      if (c.type === "date") {
        return { ...typeFloor, headerName: c.label, field: c.field, filter: "agDateColumnFilter", editable: colEditable } as ColDef<Row>;
      }
      return { ...typeFloor, headerName: c.label, field: c.field, filter: "agTextColumnFilter", editable: colEditable } as ColDef<Row>;
    });
}

export function buildSharedDefaultColDef(columnDefaults?: ColumnDefaultsResponse | null): ColDef<Row> {
  return {
    ...buildDefaultColDef(columnDefaults),
    sortable: true,
    resizable: true,
    filter: true,
    floatingFilter: true,
    flex: 1,
    minWidth: 120,
    // Hide the small funnel icon in both the floating-filter row and the header itself. The
    // header's "..." menu icon stays hidden too (EDB_CUSTOM_defaultColDef's
    // suppressHeaderMenuButton default, folded into buildDefaultColDef) - right-click on the
    // header still opens the same full column menu, so there's exactly one, uncluttered entry
    // point into filter options.
    floatingFilterComponentParams: { suppressFilterButton: true },
    suppressHeaderFilterButton: true,
    tooltipComponent: GridHeaderTooltip,
  };
}

// GRA-03/04: Filter In/Filter Out, plus Delete/Unlink and any metadata-driven custom actions
// (CRA-01/02), all from a single right-click menu. Shared between the master grid and the
// child-detail grid - only the entity context and delete/unlink handler differ between them.
export function buildRowContextMenu(
  entityName: string,
  entityMeta: EntityMeta,
  deleteLabel: string,
  onDelete: (row: Row) => void,
  onRunAction: (entityName: string, entityMeta: EntityMeta, action: ActionMeta, row: Row) => void,
) {
  return (p: GetContextMenuItemsParams<Row>): (string | MenuItemDef)[] => {
    const defaults = p.defaultItems ?? [];
    const row = p.node?.data;
    if (!row) return defaults;

    const items: (string | MenuItemDef)[] = [{ name: deleteLabel, action: () => onDelete(row) }];

    const colId = p.column?.getColId();
    const colDef = p.column?.getColDef();
    const value = p.value;
    if (colId && value != null && (colDef?.filter === "agTextColumnFilter" || colDef?.filter === "agNumberColumnFilter")) {
      const filterType = colDef?.filter === "agNumberColumnFilter" ? "number" : "text";
      items.push(
        {
          name: `Filter in "${value}"`,
          action: async () => {
            await p.api.setColumnFilterModel(colId, { filterType, type: "equals", filter: value });
            p.api.onFilterChanged();
          },
        },
        {
          name: `Filter out "${value}"`,
          action: async () => {
            await p.api.setColumnFilterModel(colId, { filterType, type: "notEqual", filter: value });
            p.api.onFilterChanged();
          },
        },
      );
    }

    if (entityMeta.actions.length > 0) {
      items.push("separator");
      for (const action of entityMeta.actions) {
        items.push({ name: action.label, action: () => onRunAction(entityName, entityMeta, action, row) });
      }
    }

    return [...items, "separator", ...defaults];
  };
}
